/**
 * Compresses an image file to ensure it is under specified size limit (default 200KB).
 * Uses canvas to resize and compress with WebP support for better compression.
 * Optimized for check-in and shop images with 200KB limit.
 */
export const compressImage = async (file: File, maxSizeKB: number = 200): Promise<File> => {
    const maxSizeBytes = maxSizeKB * 1024;

    if (file.size <= maxSizeBytes) {
        return file;
    }

    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            let width = img.width;
            let height = img.height;

            // Calculate new dimensions for 200KB target
            // 200KB is suitable for 720p-1080p range with good quality
            const MAX_WIDTH = 1280;
            const MAX_HEIGHT = 1280;

            if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                if (width > height) {
                    height = Math.round((height * MAX_WIDTH) / width);
                    width = MAX_WIDTH;
                } else {
                    width = Math.round((width * MAX_HEIGHT) / height);
                    height = MAX_HEIGHT;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error("Canvas context not available"));
                return;
            }

            ctx.drawImage(img, 0, 0, width, height);

            // Check WebP support
            const supportsWebP = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;

            // Attempt compression with WebP first for better compression
            let quality = 0.75;
            let format = supportsWebP ? 'image/webp' : 'image/jpeg';

            const attemptCompression = (q: number) => {
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error("Compression failed"));
                            return;
                        }

                        if (blob.size <= maxSizeBytes || q <= 0.1) {
                            // Success or hit min quality
                            const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, supportsWebP ? '.webp' : '.jpg'), {
                                type: format,
                                lastModified: Date.now(),
                            });
                            resolve(compressedFile);
                        } else {
                            // Try again with lower quality
                            attemptCompression(q - 0.05);
                        }
                    },
                    format,
                    q
                );
            };

            attemptCompression(quality);
        };

        img.onerror = (err) => {
            URL.revokeObjectURL(url);
            reject(err);
        };

        img.src = url;
    });
};

/**
 * Generates a thumbnail for an image file.
 * Useful for displaying previews while loading the full image.
 */
export const generateThumbnail = async (file: File, maxWidth: number = 200, maxHeight: number = 200): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            let width = img.width;
            let height = img.height;

            if (width > maxWidth || height > maxHeight) {
                if (width > height) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                } else {
                    width = Math.round((width * maxHeight) / height);
                    height = maxHeight;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error("Canvas context not available"));
                return;
            }

            ctx.drawImage(img, 0, 0, width, height);

            const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
            resolve(thumbnailUrl);
        };

        img.onerror = (err) => {
            URL.revokeObjectURL(url);
            reject(err);
        };

        img.src = url;
    });
};

/**
 * Optimizes image for web display with multiple quality levels.
 * Returns both compressed image and thumbnail.
 * Default compression target is 200KB for optimal performance.
 */
export const optimizeImage = async (file: File, maxSizeKB: number = 200): Promise<{
    compressed: File;
    thumbnail: string;
}> => {
    const compressed = await compressImage(file, maxSizeKB);
    const thumbnail = await generateThumbnail(file);
    return { compressed, thumbnail };
};
